// angular imports
import { Component, Injector, OnChanges, SimpleChanges } from '@angular/core';

// devextreme imports
import notify from 'devextreme/ui/notify';
import { custom } from 'devextreme/ui/dialog';

// cc-common imports
import { Utils } from 'cc-common';

// component imports
import { MPBaseWorkflowApprovalComponent } from '../../_base/mp-base-workflow-approval-component';

// service imports
import { WorkflowCamStepService } from '../../_services/workflow-cam-step.service';

// model imports
import { WFEEditorParameterDto } from '../../../shared-workflow-module/_models/wfe-editor-parameter-dto';
import { WFECAMStepEditorResultDto } from '../../_models/workflow-cam-step/wfe-cam-step-editor-result-dto';
import { WFEBankAccountResultDto } from '../../../shared-workflow-module/_models/wfe-bank-account-result-dto';
import { WFEDenyCAMStepParameterDto } from '../../_models/workflow-cam-step/wfe-deny-cam-step-parameter-dto';
import { APBillParameterDto } from '../../../cirabooks/shared-cirabooks-module/_models/accounts-payable/ap-bill/ap-bill-parameter-dto';
import { WFEApproveCamAcctStepParametersDto } from '../../_models/common/wfe-approve-cam-acct-step-parameters-dto';
import { WorkflowCheckListItemResultDto } from '../../../shared-workflow-module/_models/workflow-check-list-item-result-dto';
import { WorkflowChecklistEditorResultDto } from '../../../shared-workflow-module/_models/workflow-check-list-editor-result-dto';
import { WorkflowCheckListRawDto } from '../../../shared-workflow-module/_models/workflow-check-list-raw-dto';

// constant imports
import { MessageInterval } from '../../../_constants/messages-intervals';
import { WorkflowConstants } from '../../../shared-workflow-module/_constants/worflow-constants';

@Component({
    selector: 'app-workflow-cam-editor',
    templateUrl: './workflow-cam-editor.component.html',
    styleUrls: ['./workflow-cam-editor.component.scss']
})
export class WorkflowCamEditorComponent extends MPBaseWorkflowApprovalComponent implements OnChanges {
    camEditor: WFECAMStepEditorResultDto;

    selectedBankAccount: WFEBankAccountResultDto;
    description: string;

    apBillDetailParam: APBillParameterDto;

    constructor(
        protected readonly injector: Injector,
        private readonly workflowCamStepService: WorkflowCamStepService
    ) {
        super(injector);
        this.workflowStep = WorkflowConstants.CAM_STEP;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.workflowId && changes.workflowId.currentValue) {
            this.workflowId = changes.workflowId.currentValue;
        }

        if (changes.communityId && changes.communityId.currentValue) {
            this.communityId = changes.communityId.currentValue;
        }

        if (!!this.workflowId && !!this.communityId) {
            this.resetWorkflow();
            setTimeout(() => {
                this.loadMoveToCAMStep();
            }, 250);
        }
    }

    onForceRefresh(forceRefresh: boolean): void {
        if (Utils.isTrue(forceRefresh)) {
            this.loadMoveToCAMStep(forceRefresh);
        }
    }

    onCAMWorkflowClose(): void {
        this.closeWorkflowCAMStep();
    }

    onBankAccountChange(selectedBankAccount: WFEBankAccountResultDto): void {
        super.populateBankBalance(selectedBankAccount.bankAccountId);
        this.selectedBankAccount = selectedBankAccount;
    }

    onDenyCAMReasonChange(reason: string): void {
        this.finalizeWorkflowEditor();
        if (Utils.isTrue(this.isValidWorkflowToApprove)) {
            this.denyCamStep(reason);
        }
    }

    onPlaceInReviewClick(): void {
        this.finalizeWorkflowEditor();
        if (Utils.isTrue(this.isValidWorkflowToApprove)) {
            this.placeInReviewWorkflow();
        }
    }

    onApproveClick(): void {
        this.finalizeWorkflowEditor();
        if (Utils.isTrue(this.isValidWorkflowToApprove)) {
            if (
                Utils.equalsIgnoreCase(this.camEditor.workflow.payeeType, WorkflowConstants.PAYEE_TYPE_VENDOR) &&
                Utils.notNullOrEmpty(this.camEditor.managementCompanyVendorId)
            ) {
                this.prepareLinkedWorkOrders();
            } else {
                this.prepareVendorChecklistAndApproveCAMStep();
            }
        }
    }

    onDescriptionUpdate(desc: string): void {
        this.description = desc;
    }

    onCamReclassifyClick(): void {
        this.finalizeWorkflowEditor();
        if (Utils.isTrue(this.isValidWorkflowToApprove)) {
            this.reclassBill(this.workflowStep);
        }
    }

    onCamUpdateBillClick(): void {
        this.finalizeWorkflowEditor();
        if (Utils.isTrue(this.isValidWorkflowToApprove)) {
            this.updateBill(this.workflowStep);
        }
    }

    onNotifyAndCloseVendorChecklist(checkListItems: Array<WorkflowCheckListItemResultDto>): void {
        this.workflowEditedChecklist = this.getEditedChecklist(checkListItems, this.workflowStep);
        if (Utils.isTrue(this.isChecklistApprovable)) {
            this.approveCAMStepWorkflow();
        } else {
            setTimeout(() => (this.showVendorChecklist = true), 500);
        }
    }

    onVendorChecklistShow(): void {
        this.isApproveAction = false;
        this.populateChecklistItems();
    }

    onSaveChecklist(checklistItems: Array<WorkflowCheckListRawDto>): void {
        this.saveCheckList(checklistItems, this.workflowStep);
    }

    onMarkWorkOrderComplete(workOrderIds: Array<number>): void {
        this.workOrderToComplete = workOrderIds;
        this.showWorkOrder = false;
        this.prepareVendorChecklistAndApproveCAMStep();
    }

    private prepareLinkedWorkOrders(): void {
        this.showOnlyLinkedWorkOrder = true;
        this.showWorkOrder = true;
    }

    private populateChecklistItems(): void {
        const baseParam: WFEEditorParameterDto = new WFEEditorParameterDto();
        baseParam.communityId = this.communityId;
        baseParam.workflowId = this.workflowId;
        baseParam.clearCache = true;
        this.workflowEditorSharedService.getWorkflowChecklistEditor(baseParam, this.workflowStep).subscribe(
            (result: WorkflowChecklistEditorResultDto) => {
                this.checkListEditor = result;
                this.checklistItems = result.checklistItems;
                this.prepareCheckListItems();

                if (Utils.isFalse(this.isApproveAction)) {
                    this.showVendorChecklist = true;
                } else {
                    this.workflowEditedChecklist = this.getEditedChecklist(this.checklistItems, this.workflowStep);
                    if (Utils.isTrue(this.isChecklistApprovable)) {
                        this.approveCAMStepWorkflow();
                    } else {
                        this.showVendorChecklist = true;
                    }
                }
            },
            (error) => {
                this.errorService.displayError(error);
            }
        );
    }

    private prepareCheckListItems(): void {
        this.checklistItems.forEach((item: WorkflowCheckListItemResultDto) => {
            const camItem = this.checkListEditor.camChecklistStatuses.find((d) => d.id === item.id);

            item['camStatus'] = Utils.notNullAndDefined(camItem) ? camItem['status'] : null;
            item['camComments'] = Utils.notNullAndDefined(camItem) ? camItem['comments'] : null;
        });
    }

    private updateWorkflowEditorChanges(): void {
        const workflowInvoiceHeaderDetail = this.invoiceHeaderDetailEditor.getWorkflowEditor();
        this.workflowEditor.postDate = workflowInvoiceHeaderDetail.postDate;
        this.workflowEditor.isEFTAccount = workflowInvoiceHeaderDetail.isEFTAccount;
        this.workflowEditor.lateFee = workflowInvoiceHeaderDetail.lateFee;
        this.workflowEditor.specialHandlingId = workflowInvoiceHeaderDetail.specialHandlingId;
        this.workflowEditor.boardApprovalRequired = workflowInvoiceHeaderDetail.boardApprovalRequired;
    }

    private prepareWorkflowLineItems(): void {
        this.isValidWorkflowToApprove = this.workflowLineItemEditor.isValidLineItems();
        if (Utils.isTrue(this.isValidWorkflowToApprove)) {
            const modifiedLineItems = this.workflowLineItemEditor.getModifiedLineItems();
            this.checkIfTargetCommunityAvailable(modifiedLineItems);
            this.workflowEditor.items = modifiedLineItems;
        }
    }

    private prepareVendorChecklistAndApproveCAMStep(): void {
        if (Utils.isTrue(this.camEditor.hasVendorWFCheckList)) {
            this.isApproveAction = true;
            if (Utils.isNullOrEmpty(this.workflowEditedChecklist)) {
                this.populateChecklistItems();
            } else {
                const notEditedChecklist = this.workflowEditedChecklist.find((d) => !d.status);
                if (Utils.notNullAndDefined(notEditedChecklist)) {
                    this.populateChecklistItems();
                } else {
                    this.approveCAMStepWorkflow();
                }
            }
        } else {
            this.workflowEditedChecklist = [];
            this.approveCAMStepWorkflow();
        }
    }

    private approveCAMStepWorkflow(): void {
        const baseParams: WFEApproveCamAcctStepParametersDto = new WFEApproveCamAcctStepParametersDto();
        baseParams.workflowChecklistStatus = this.workflowEditedChecklist;
        baseParams.workflow = this.workflowEditor;
        baseParams.closeWorkOrderIds = this.workOrderToComplete;
        baseParams.createTemplate = false;

        if (
            Utils.isFalse(this.isLineItemsTargetCommunityAvailable) &&
            Utils.isFalse(this.isDefaultCodingLineItemAvailable) &&
            Utils.equalsIgnoreCase(this.workflowEditor.payeeType, WorkflowConstants.PAYEE_TYPE_VENDOR) &&
            Utils.isTrue(this.camEditor.isActiveCommunityVendor)
        ) {
            const customDialog = custom({
                title: 'Confirmation',
                messageHtml: `<b>Do you want to save the line items as default template for this vendor in this community ?`,
                buttons: [
                    {
                        text: 'Yes',
                        type: 'default',
                        onClick: () => {
                            baseParams.createTemplate = true;
                            this.approveWorkflow(baseParams);
                        }
                    },
                    {
                        text: 'No',
                        type: 'default',
                        onClick: () => {
                            baseParams.createTemplate = false;
                            this.approveWorkflow(baseParams);
                        }
                    }
                ]
            });
            customDialog.show();
        } else {
            this.approveWorkflow(baseParams);
        }
    }

    private approveWorkflow(baseParams: WFEApproveCamAcctStepParametersDto): void {
        this.loadingVisible = true;
        this.workflowCamStepService.approve(baseParams).subscribe(
            (result: any) => {
                const message = `Workflow ${this.workflowId} is successfully approved.`;
                notify(message, 'Success', MessageInterval.successMsgInterval);
                this.closeOrExitWorkflowEditor(true);
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private placeInReviewWorkflow(): void {
        this.loadingVisible = true;

        this.workflowCamStepService.placeInReview(this.workflowEditor).subscribe(
            (result: any) => {
                this.showCamStepUpdatedMessage();
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private denyCamStep(reason: string): void {
        const baseParams: WFEDenyCAMStepParameterDto = new WFEDenyCAMStepParameterDto();
        baseParams.denyReason = reason;
        baseParams.workflow = this.workflowEditor;

        this.loadingVisible = true;
        this.workflowCamStepService.denyCam(baseParams).subscribe(
            (result: any) => {
                this.showCamStepUpdatedMessage();
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private showCamStepUpdatedMessage(): void {
        const message = `Workflow ${this.workflowId} is successfully updated.`;
        notify(message, 'Success', MessageInterval.successMsgInterval);
        this.closeOrExitWorkflowEditor(true);
    }

    private loadMoveToCAMStep(clearCache = false): void {
        const baseParams: WFEEditorParameterDto = new WFEEditorParameterDto();
        baseParams.communityId = this.communityId;
        baseParams.workflowId = this.workflowId;
        baseParams.clearCache = clearCache;

        this.loadingVisible = true;
        this.workflowCamStepService.getWorflowMoveToCAMEditor(baseParams).subscribe(
            (result: WFECAMStepEditorResultDto) => {
                this.camEditor = result;
                this.workflowEditor = this.camEditor.workflow;
                this.breadCrumbs = this.camEditor.wfStatusBreadcrumb;
                this.bankBalance = this.camEditor.bankBalance;
                this.dimensionSource = this.camEditor.dimensions;

                if (Utils.notNullAndDefined(this.workflowEditor.bankAccountId)) {
                    this.selectedBankAccount = this.camEditor.bankAccounts.find((d) =>
                        Utils.equals(d.bankAccountId, this.workflowEditor.bankAccountId)
                    );
                }

                this.getDefaultsComponentValues();
                if (Utils.equalsIgnoreCase(this.workflowEditor.payeeType, WorkflowConstants.PAYEE_TYPE_VENDOR)) {
                    this.populateCodingDefaultEditor(this.workflowStep);
                }
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
                this.closeWorkflowCAMStep();
            }
        );
    }

    private finalizeWorkflowEditor(): void {
        this.updateWorkflowEditorChanges();
        this.prepareWorkflowLineItems();
    }

    private closeWorkflowCAMStep(): void {
        this.resetWorkflowCAM();
        super.onWorkflowClose();
    }

    private resetWorkflowCAM(): void {
        this.camEditor = null;
        this.workflowEditor = null;
        this.breadCrumbs = [];
        this.workflowId = null;
        this.communityId = null;
        this.bankBalance = null;
        this.dimensionSource = [];
    }
}
